/**
 * A aba aberta mora na URL.
 *
 * Uma aba que só existe na memória do componente quebra três coisas que as pessoas fazem o dia
 * inteiro sem pensar: recarregar a página e voltar para onde estava; mandar o link para alguém e
 * essa pessoa ver a mesma tela; e apertar «voltar» depois de trocar de aba. Sem isto, F5 na aba
 * «Onde está» devolvia a ficha na primeira aba, e o link colado num chat sempre abria o começo,
 * ainda que a conversa fosse sobre o mapa.
 *
 * O fragmento (`#onde-esta`) e não a query: a aba é uma âncora dentro do MESMO recurso, não um
 * filtro sobre ele. E fragmento não vai ao servidor, então nada disso muda uma requisição.
 *
 * ## O slug vem do RÓTULO, e o id continua valendo
 *
 * `#onde-esta` diz o que a aba é; `#localizacao` — o id interno — diz o que o programador chamou.
 * Quem lê a URL é gente, então a forma canônica é o rótulo. Mas o id continua sendo aceito na
 * entrada, para que um link antigo (ou um id digitado à mão por quem leu o código) não caia em
 * lugar nenhum. Rótulo que muda invalida o link antigo, e aí a tela abre na primeira aba em vez de
 * numa aba errada — degradação previsível é preferível a link eterno com conteúdo trocado.
 */
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/** «Pôr para funcionar» → `por-para-funcionar`. Sem acento, sem maiúscula, sem pontuação. */
export function slugAba(rotulo: string): string {
  return rotulo
    // `NFD` separa a letra do acento; o `\p{Diacritic}` varre os acentos soltos. Escrito por
    // propriedade Unicode, e não por faixa de códigos literal, porque a faixa vira caractere
    // invisível no arquivo — sobrevive mal a editor, a diff e a quem for ler isto depois.
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

type AbaMinima = { id: string; rotulo: string };

/**
 * Devolve a aba ativa (pelo `id`) e a função que troca de aba escrevendo na URL.
 *
 * Com a lista vazia, devolve `''` e uma função inerte — recursos sem abas passam por aqui sem
 * ganhar hash nenhum, e o gancho não precisa ser chamado condicionalmente.
 */
export function useAbaNaUrl(abas: AbaMinima[] | undefined): [string, (id: string) => void] {
  const { pathname, search, hash } = useLocation();
  const navegar = useNavigate();

  const lista = abas ?? [];
  const alvo = decodeURIComponent(hash.replace(/^#/, ''));
  const encontrada = lista.find((a) => slugAba(a.rotulo) === alvo || a.id === alvo);
  const ativa = encontrada?.id ?? lista[0]?.id ?? '';
  const canonico = encontrada ? slugAba(encontrada.rotulo) : slugAba(lista[0]?.rotulo ?? '');

  // Normaliza a URL para a forma canônica: sem hash (acabou de abrir) ou com um hash que não
  // corresponde a aba nenhuma (link velho, rótulo renomeado, dedo pesado). `replace` porque isto
  // é arrumação nossa, não navegação do usuário — deixar rastro no histórico faria o «voltar»
  // gastar um clique para sair de uma correção que ninguém pediu.
  useEffect(() => {
    if (!canonico || alvo === canonico) return;
    navegar(`${pathname}${search}#${canonico}`, { replace: true });
  }, [canonico, alvo, pathname, search, navegar]);

  const trocar = useCallback(
    (id: string) => {
      const aba = lista.find((a) => a.id === id);
      if (!aba) return;
      // `push`, ao contrário da normalização: trocar de aba é navegação de verdade, e o «voltar»
      // tem de devolver a aba anterior — é o que a pessoa espera depois de clicar.
      navegar(`${pathname}${search}#${slugAba(aba.rotulo)}`);
    },
    // `lista` é recriada a cada render (vem de um descritor estático); depender dela recriaria
    // `trocar` sem parar. O que importa para a identidade é a rota, e essa está nas dependências.
    [pathname, search, navegar],
  );

  return [ativa, trocar];
}
